<script lang="ts">
    import axios from 'axios';
    import Shgrid from '../../components/shgrid/shgrid.svelte';
    import type {Column} from '@/components/shgrid';
    import type {VehicleAssignment} from "@/types/types";
    import {SvelteMap} from "svelte/reactivity";
    import { CalendarDays, FileUp, FileCheck, Download } from 'lucide-svelte';
    import RamsUploadModal from './RamsUploadModal.svelte';
    import * as Tooltip from '@/components/ui/tooltip';
    import * as Table from '@/components/ui/table/index.js';
    import { inertia } from '@inertiajs/svelte';
    import PicklistBadge from '@/components/ui/picklist-badge/PicklistBadge.svelte';

    type Row = VehicleAssignment;

    type SelectOption = {
        value: string;
        label: string;
    };

    type Props = {
        view : string;
        relatedData?: Record<string, any>;
        selectedVehicleAssignments?: SvelteMap<string, string> | undefined;
        onSubmitClick?: () => void;
        title?: string | null;
        id?: string;
        jobTypeOptions?: SelectOption[];
    };

    let {view, relatedData, selectedVehicleAssignments = $bindable(), onSubmitClick, title = null, id, jobTypeOptions = []}: Props = $props();
    const getRowLink = (row: Row) => route('jobs.show', {job: row.culmex_job_id});
    // Include links for non-related views
    const includeLink = view != 'related' && view !== 'relatedList';

    // Refetch function from shgrid
    let refetch: (() => Promise<any>) | undefined = $state();
    // Modal state
    let showRamsModal = $state(false);
    let selectedAssignment = $state<VehicleAssignment | null>(null);
    function openRamsModal(assignment: VehicleAssignment) {
        selectedAssignment = assignment;
        showRamsModal = true;
    }

    function closeRamsModal() {
        showRamsModal = false;
        selectedAssignment = null;
    }

    function getSchedulerUrl(row: Row): string {
        const startDate = row.start_date_time ? new Date(row.start_date_time).toISOString().split('T')[0] : '';
        return route('schedular.index', {
            highlightedAssignmentId: row.id,
            displayDate: startDate
        });
    }

    function getJobCardDocument(row: Row) {
        return row.documents?.find((doc) => doc.filepath?.includes('documents/job-cards/')) ?? null;
    }

    function hasRamsDocument(row: Row): boolean {
        if(!(row.documents && row.documents.length > 0)) return false;

        const ramsDoc = row.documents.find(
            (doc) => doc.title?.toLowerCase()?.includes('rams')
        );

        return !!ramsDoc;
    }

    let cols = $state<Column<Row>[]>([
        {
            label: 'Vehicle',
            key: 'vehicle',
            cellRender: {
                type: 'text',
                label: (row) => row.vehicle ? `${row.vehicle.make} ${row.vehicle.model} (${row.vehicle.registration})` : 'Unassigned',
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'string',
            },
        },
        {
            label: 'Job Type',
            key: 'job_type',
            cellRender: {
                type: 'custom',
                snippet: jobTypeBadgeSnippet,
            },
            sortable: true,
            filter: jobTypeOptions.length > 0
                ? { type: 'select', options: jobTypeOptions }
                : { type: 'string' },
        },
        {
            label: 'Phase',
            key: 'job_phase',
            cellRender: {
                type: 'text',
                label: (row) => row.job_phase
                    ? `Phase ${row.job_phase.sequence} — ${row.job_phase.label}`
                    : 'Primary',
                ...(includeLink && { link: getRowLink }),
            },
            sortable: false,
            filter: { type: 'string' },
        },
        {
            label: 'Postcode',
            key: 'postcode',
            cellRender: {
                type: 'text',
                label: (row) => row.address?.postcode || row.job?.address?.postcode || 'N/A',
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'string',
            },
        },
        {
            label: 'Start Date',
            key: 'start_date_time',
            cellRender: {
                type: 'text',
                label: (row) => row.start_date_time ? new Date(row.start_date_time).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                }) : '',
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'string',
            },
        },
        {
            label: 'End Date',
            key: 'end_date_time',
            cellRender: {
                type: 'text',
                label: (row) => row.end_date_time ? new Date(row.end_date_time).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                }) : '',
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'string',
            },
        },
        {
            label: 'Site',
            key: 'site_name',
            cellRender: {
                type: 'text',
                label: (row) => row.job?.site_name || 'N/A',
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'string',
            },
        },
        {
            label: 'Employees',
            key: 'employeeAssignments',
            cellRender: {
                type: 'text',
                label: (row) => {
                    if (!row.employeeAssignments || row.employeeAssignments.length === 0) {
                        return 'None';
                    }
                    return row.employeeAssignments
                        .map((ea: any) => ea.employee?.name || 'Unknown')
                        .join(', ');
                },
                ...(includeLink && { link: getRowLink }),
            },
            sortable: false,
            filter: {
                type: 'string',
            },
        },
        {
            label: 'Actions',
            key: 'actions',
            cellRender: {
                type: 'custom',
                snippet: actionsSnippet
            },
            sortable: false,
        },
    ]);

</script>

{#snippet jobTypeBadgeSnippet(row: Row)}
    <Table.Cell>
        {#if row.job_type_label}
            <PicklistBadge label={row.job_type_label} color={row.job_type_color ?? 'gray'} />
        {:else}
            <span class="text-muted-foreground">N/A</span>
        {/if}
    </Table.Cell>
{/snippet}

{#snippet actionsSnippet(row: Row)}
    <Tooltip.Provider delayDuration={0}>
        <div class="flex items-center gap-2 justify-center">
            <!-- Scheduler link -->
            <Tooltip.Root>
                <Tooltip.Trigger>
                    <a
                        href={getSchedulerUrl(row)}
                        use:inertia
                        class="p-1.5 rounded hover:bg-muted transition-colors"
                        onclick={(e) => e.stopPropagation()}
                    >
                        <CalendarDays class="h-4 w-4 text-muted-foreground hover:text-foreground" />
                    </a>
                </Tooltip.Trigger>
                <Tooltip.Content>
                    <p>View in Scheduler</p>
                </Tooltip.Content>
            </Tooltip.Root>

            <!-- Job card download -->
            {#if getJobCardDocument(row)}
                <Tooltip.Root>
                    <Tooltip.Trigger>
                        <a
                            href={route('documents.download', { documentId: getJobCardDocument(row)!.id })}
                            class="p-1.5 rounded hover:bg-muted transition-colors"
                            onclick={(e) => e.stopPropagation()}
                            download
                        >
                            <Download class="h-4 w-4 text-muted-foreground hover:text-foreground" />
                        </a>
                    </Tooltip.Trigger>
                    <Tooltip.Content>
                        <p>{getJobCardDocument(row)!.title ?? 'Download Job Card'}</p>
                    </Tooltip.Content>
                </Tooltip.Root>
            {/if}

            <!-- RAMS upload -->
            <Tooltip.Root>
                <Tooltip.Trigger>
                    <button
                        type="button"
                        class="p-1.5 rounded hover:bg-muted transition-colors"
                        onclick={(e) => {
                            e.stopPropagation();
                            openRamsModal(row);
                        }}
                    >
                        {#if hasRamsDocument(row)}
                            <FileCheck class="h-4 w-4 text-green-600" />
                        {:else}
                            <FileUp class="h-4 w-4 text-muted-foreground hover:text-foreground" />
                        {/if}
                    </button>
                </Tooltip.Trigger>
                <Tooltip.Content>
                    <p>{hasRamsDocument(row) ? 'View RAMS' : 'Upload RAMS'}</p>
                </Tooltip.Content>
            </Tooltip.Root>
        </div>
    </Tooltip.Provider>
{/snippet}

<!-- RAMS Upload Modal -->
{#if showRamsModal && selectedAssignment}
    <RamsUploadModal
        bind:show={showRamsModal}
        vehicleAssignment={selectedAssignment}
        onClose={closeRamsModal}
        onDocumentUploaded={() => refetch?.()}
    />
{/if}

<div {id} class="mb-4">
    <Shgrid
        fetcher={async ({ limit, offset, filters, sorter }) => {
            if (view === 'relatedList' && relatedData) {
                let relatedColumn = relatedData.column;
                let record_id = relatedData.record_id;
                filters = { ...filters, [relatedColumn]: record_id };
            }
            const res = await axios.get(route('web-api.VehicleAssignment.index', { limit, offset, filters, sorter }));
            return { data: res.data, error: null };
        }}
        limit={100}
        bind:columns={cols}
        bind:selected={selectedVehicleAssignments}
        bind:refetch
        {...(view === 'multi_related' ? { onSubmitClick } : {})}
        title={title}
    />
</div>
